import type { AuthUser } from '@yoreflix/core/api/types'

/**
 * The permission check, client-side.
 *
 * ⚠ THIS IS A UI CONCERN ONLY. It decides what to SHOW. It is not a security boundary and must
 * never be treated as one: the real gate is server-side, where every `yoreflix/v1/upload/*`
 * route is wrapped in `Guard::protect(..., 'upload.*')` AND resolves the caller's organization
 * from their session row (`OrgScope`). Hiding a menu entry here stops an honest user taking a
 * route that would 403; it stops nobody else.
 *
 * ★★ AND NOTE WHAT THIS FILE CANNOT DO: it cannot scope data. Whether this account may use the
 * portal is decided here; WHOSE data it sees is decided exclusively on the server, from the
 * session, and is not expressible as a client-side check at all. That separation is deliberate —
 * v1's provider endpoints trusted a client-supplied identifier and let any authenticated
 * provider reach another supplier's records.
 *
 * Semantics MIRROR the backend's `Auth\Authorization::userCan()` so the two cannot drift:
 * effective permissions are the union of the roles' permissions (the backend pre-resolves them
 * into `AuthUser.Permissions`), a grant ending in `.*` is a wildcard, matching is otherwise exact.
 */

/** The permission that admits a user to this portal at all (config/roles.php: `uploader`). */
export const PORTAL_PERMISSION_PREFIX = 'upload.'

/**
 * Does this permission set grant `permission`?
 *
 * @param granted the user's effective permissions (`AuthUser.Permissions`)
 */
export function permissionsAllow(granted: readonly string[], permission: string): boolean {
  for (const grant of granted) {
    if (grant === permission) return true
    // `upload.*` grants `upload.files`: strip the `*` and prefix-match. The kept dot
    // matters — `slice(0, -1)` leaves `upload.`, so `uploadfoo` cannot match `upload.*`.
    if (grant.endsWith('.*') && permission.startsWith(grant.slice(0, -1))) return true
  }
  return false
}

/** Does this user hold `permission`? A null user (signed out) holds nothing. */
export function userCan(user: AuthUser | null, permission: string): boolean {
  if (!user) return false
  return permissionsAllow(user.Permissions ?? [], permission)
}

/**
 * May this user use the upload portal at all?
 *
 * True when they hold ANY `upload.` permission (the `uploader` role). A signed-in viewer
 * (`watch` / `free`) — or even a `provider` who was given read-only dashboard access but NOT
 * upload rights — is a real, valid Yoreflix account that simply has no business here: they
 * authenticate fine and are then refused the portal, which is a different outcome from "bad
 * password" and must read differently to the user.
 *
 * ⚠ AN ADMIN IS NOT AUTOMATICALLY AN UPLOADER, and that is on purpose. `admin.*` does not match
 * `upload.`, so staff cannot wander into a supplier's write surface. Nor does `provider.*` — the
 * two supplier roles are deliberately separable (a supplier may read their dashboard without the
 * ability to push masters at us). Every route still re-checks org ownership server-side.
 */
export function canUsePortal(user: AuthUser | null): boolean {
  if (!user) return false
  const granted = user.Permissions ?? []
  return granted.some((grant) => grant.startsWith(PORTAL_PERMISSION_PREFIX))
}
